import type { FormEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CafeModerationStatus } from '@cookspace-shared-contracts/core';
import { Eye } from 'lucide-react';

import { useListCafeApplicationsQuery } from '@/api/cafeApplicationsApi';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';

const STATUS_OPTIONS: Array<{ value: CafeModerationStatus; label: string }> = [
    { value: 'pending', label: 'На рассмотрении' },
    { value: 'approved', label: 'Одобрена' },
    { value: 'rejected', label: 'Отклонена' }
];

const STATUS_LABEL: Record<CafeModerationStatus, string> = {
    pending: 'На рассмотрении',
    approved: 'Одобрена',
    rejected: 'Отклонена'
};

const STATUS_COLOR: Record<CafeModerationStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
};

const PAGE_SIZE = 20;

function formatDate(iso: string | null): string {
    if (iso === null) {
        return '—';
    }
    return new Date(iso).toLocaleDateString('ru-RU');
}

export function CafeApplicationsPage(): ReactElement {
    const navigate = useNavigate();

    const [filterStatus, setFilterStatus] = useState<CafeModerationStatus | ''>('pending');
    const [searchInput, setSearchInput] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(0);

    const queryArg = useMemo(
        () => ({
            ...(filterStatus ? { status: filterStatus } : {}),
            ...(searchValue !== '' ? { search: searchValue } : {}),
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE
        }),
        [filterStatus, searchValue, page]
    );

    const { data, isLoading, isError } = useListCafeApplicationsQuery(queryArg);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE);

    const handleSearchSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        setSearchValue(searchInput.trim());
        setPage(0);
    };

    return (
        <div data-testid="cafe-applications-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Заявки кафе</h1>
            </div>

            <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3">
                <div className="w-56">
                    <Select
                        label="Статус"
                        value={filterStatus}
                        onChange={(e) => {
                            setFilterStatus(e.target.value as CafeModerationStatus | '');
                            setPage(0);
                        }}
                        placeholder="Все статусы"
                        options={STATUS_OPTIONS}
                        data-testid="cafe-applications-filter-status"
                    />
                </div>
                <form
                    onSubmit={handleSearchSubmit}
                    className="flex w-72 items-end gap-2"
                    data-testid="cafe-applications-search-form"
                >
                    <div className="flex-1">
                        <Input
                            label="Поиск (ИНН / название / владелец)"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="7700000000"
                            data-testid="cafe-applications-search-input"
                        />
                    </div>
                    <button
                        type="submit"
                        className="rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                        data-testid="cafe-applications-search-submit"
                    >
                        Найти
                    </button>
                </form>
            </div>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="cafe-applications-error">
                    Не удалось загрузить заявки
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="cafe-applications-table"
                >
                    <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.3fr)_130px_120px_140px_120px_100px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>Название</span>
                        <span>Юр. название</span>
                        <span>ИНН</span>
                        <span>Откл. раз</span>
                        <span>Статус</span>
                        <span>Создана</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Заявки не найдены
                        </div>
                    )}

                    {items.map((item) => (
                        <div
                            key={item.cafeId}
                            className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.3fr)_130px_120px_140px_120px_100px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`cafe-application-row-${item.cafeId}`}
                        >
                            <span className="truncate text-gray-900" title={item.name}>
                                {item.name}
                            </span>
                            <span
                                className="truncate text-gray-700"
                                title={item.nameShort ?? undefined}
                            >
                                {item.nameShort ?? '—'}
                            </span>
                            <span className="truncate font-mono text-xs text-gray-700">
                                {item.inn ?? '—'}
                            </span>
                            <span
                                className="text-gray-700"
                                data-testid={`cafe-application-rejections-${item.cafeId}`}
                            >
                                {item.rejectionsCount > 0 ? (
                                    <span className="inline-block rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-800">
                                        {item.rejectionsCount}
                                    </span>
                                ) : (
                                    '—'
                                )}
                            </span>
                            <span>
                                <span
                                    className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLOR[item.moderationStatus]}`}
                                >
                                    {STATUS_LABEL[item.moderationStatus]}
                                </span>
                            </span>
                            <span className="text-xs text-gray-700">
                                {formatDate(item.createdAt)}
                            </span>
                            <span className="flex justify-end gap-1">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/cafe-applications/${item.cafeId}`)}
                                    data-testid={`cafe-application-view-${item.cafeId}`}
                                    className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-100"
                                    aria-label="Открыть"
                                    title="Открыть"
                                >
                                    <Eye size={12} />
                                    Открыть
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div
                    className="flex items-center justify-between text-sm text-gray-700"
                    data-testid="cafe-applications-pagination"
                >
                    <span>
                        Страница {page + 1} из {totalPages} · всего {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="cafe-applications-page-prev"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Назад
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page + 1 >= totalPages}
                            data-testid="cafe-applications-page-next"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Вперёд
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
